<template>
  <div class="messaging-safety mt-3 p-3">
    <h5 class="mb-1">🛡️ Messaging Safety — kill switches</h5>
    <p class="text-muted mb-3" style="font-size: 0.85rem;">
      Three independent stops for one practice — or everywhere — in an emergency. Everything here is
      <strong>reversible</strong> and <strong>never releases a phone number.</strong>
      <span v-if="status && status.policyCounts" class="d-block">
        WhatsApp-only practices (outbound texting off): {{ status.policyCounts.smsOutbound || 0 }}.
      </span>
    </p>

    <div v-if="loading" class="text-muted py-3"><i class="fa fa-spinner fa-spin mr-1"></i>Loading status…</div>
    <b-alert v-else-if="!status" show variant="danger">Couldn't load messaging-kill status. <b-button size="sm" variant="link" @click="refresh">Retry</b-button></b-alert>

    <template v-else>
      <b-alert v-if="!status.enforcementEnabled" show variant="warning" class="py-2" style="font-size: 0.85rem;">
        <strong>Enforcement is OFF for this environment.</strong> The three emergency switches still save state, but
        nothing is actually blocked until <code>MESSAGING_KILL_ENABLED</code> is turned on. (Safe to test those buttons.)
        The <strong>WhatsApp-only</strong> setting is not an emergency switch — it always applies.
      </b-alert>

      <!-- Global — one row per switch, each independently armed -->
      <div class="ms-global mb-3 p-3" :class="{ 'ms-global--armed': anyGlobalActive }">
        <div v-for="sw in SWITCHES" :key="'g-' + sw.key" class="d-flex justify-content-between align-items-center flex-wrap py-2" style="gap: 10px;">
          <div>
            <strong :class="status.globalKills[sw.key] ? 'text-danger' : ''">
              {{ status.globalKills[sw.key] ? ('🛑 ' + sw.globalOnLabel) : (sw.label + ' — not active globally') }}
            </strong>
            <span class="d-block text-muted" style="font-size: 0.8rem; max-width: 560px;">
              {{ sw.globalHelp }}
              ({{ status.killedCount[sw.key] }} of {{ status.sites.length }} practices currently affected.)
            </span>
          </div>
          <div class="text-nowrap">
            <b-button v-if="status.globalKills[sw.key]" variant="success" size="sm" :disabled="busy" @click="setGlobal(sw, false)">
              ▶ Restore {{ sw.short }} everywhere
            </b-button>
            <template v-else>
              <b-button
                v-if="globalArming !== sw.key"
                variant="danger" size="sm" :disabled="busy"
                v-b-popover.hover.left.html="popoverFor(sw, false, true)"
                @click="globalArming = sw.key; globalConfirmText = ''"
              >🛑 {{ sw.globalButton }}…</b-button>
              <span v-else class="d-inline-flex align-items-center" style="gap: 6px;">
                <b-form-input v-model="globalConfirmText" size="sm" placeholder="type KILL" style="width: 110px;" />
                <b-button variant="danger" size="sm" :disabled="globalConfirmText !== 'KILL' || busy" @click="setGlobal(sw, true)">Confirm</b-button>
                <b-button variant="link" size="sm" class="p-0" @click="globalArming = null; globalConfirmText = ''">cancel</b-button>
              </span>
            </template>
          </div>
        </div>
      </div>

      <!-- Per-site -->
      <div class="table-responsive">
        <table class="table table-sm ms-table mb-1">
          <thead><tr><th>Practice</th><th>Status</th><th class="text-right">Emergency switches</th><th class="ms-policy-col">WhatsApp-only</th></tr></thead>
          <tbody>
            <tr v-for="s in status.sites" :key="s.siteID">
              <td class="align-middle">{{ s.name }}</td>
              <td class="align-middle">
                <template v-if="activeKills(s).length">
                  <b-badge v-for="k in activeKills(s)" :key="s.siteID + k.key" :variant="k.variant" class="mr-1">{{ k.badge }}</b-badge>
                </template>
                <b-badge v-else variant="success">Active</b-badge>
              </td>
              <td class="text-right align-middle">
                <b-button
                  v-for="sw in SWITCHES"
                  :key="s.siteID + '-' + sw.key"
                  size="sm"
                  class="mr-1"
                  :variant="s.kills[sw.key] ? 'success' : sw.variant"
                  :disabled="busy"
                  v-b-popover.hover.left.html="popoverFor(sw, s.kills[sw.key])"
                  @click="setSite(s, sw, !s.kills[sw.key])"
                >{{ s.kills[sw.key] ? ('▶ Restore ' + sw.short) : sw.button }}</b-button>
              </td>
              <!-- Per-site STANDING POLICY (SMS_OUTBOUND_DISABLED), not an emergency kill: outbound SMS off,
                   patients messaged on WhatsApp, inbound SMS still arrives. Per-site only — there is no
                   global button (a global restore would wipe it for every WhatsApp-only office). -->
              <td class="align-middle ms-policy-col" :data-test="'sms-outbound-' + s.siteID">
                <b-badge v-if="smsOutboundControl(s).on" variant="info" class="mr-1">WhatsApp-only</b-badge>
                <b-badge v-if="smsOutboundControl(s).warning" variant="danger" class="mr-1" v-b-popover.hover.left="smsOutboundControl(s).warning">Can't message patients</b-badge>
                <span v-b-popover.hover.left="smsOutboundControl(s).disabledReason || ''" class="d-inline-block">
                  <b-button
                    size="sm"
                    :variant="smsOutboundControl(s).on ? 'outline-success' : 'outline-info'"
                    :disabled="busy || !smsOutboundControl(s).canToggle"
                    @click="setSmsOutbound(s, !smsOutboundControl(s).on)"
                  >{{ smsOutboundControl(s).on ? 'Turn texting back on' : 'Make WhatsApp-only' }}</b-button>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <b-button variant="link" size="sm" class="p-0" :disabled="busy" @click="refresh"><i class="fa fa-refresh mr-1"></i>Refresh</b-button>
    </template>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { actionOutcome, smsOutboundControl, smsOutboundPayload } from '@/utils/messagingSafety';

export default {
  name: 'MessagingSafety',
  data() {
    return {
      loading: true,
      busy: false,
      status: null,
      globalArming: null,      // which switch key is mid-confirmation (null = none)
      globalConfirmText: '',
      // THREE INDEPENDENT switches, one per button — replaced a single enum ('campaigns' | 'all')
      // on 2026-08-05. The old model could not express "kill email but not SMS", and its name
      // ("messaging") read as everything when it only ever covered Twilio.
      // Copy is deliberately concrete about BLAST RADIUS: an operator reaching for these is usually
      // under pressure and must not have to guess what still gets through.
      SWITCHES: [
        {
          key: 'twilio',
          label: 'Twilio messaging',
          short: 'Twilio',
          button: 'Kill Twilio',
          badge: 'Twilio KILLED',
          variant: 'outline-danger',
          badgeVariant: 'danger',
          globalButton: 'Kill ALL Twilio',
          globalOnLabel: 'ALL Twilio is KILLED platform-wide',
          globalHelp: 'Halts ALL outbound Twilio for EVERY practice — texts, WhatsApp, reminders, campaigns. Emergency use only.',
          stops: ['Appointment reminders &amp; recall texts', 'Patient chat replies (in-house Conversations)', 'Campaign SMS', 'WhatsApp messages', 'Booking confirmations &amp; OTP texts', 'Tx-presenter links'],
          keeps: ['ALL email — reminders, recall, campaigns', 'Incoming texts still arrive (you just cannot reply)', 'Everything else in Teamio'],
          note: 'The phone number is KEPT and never released. Takes effect within ~15 seconds.',
        },
        {
          key: 'campaign',
          label: 'Campaigns',
          short: 'campaigns',
          button: 'Kill Campaigns',
          badge: 'Campaigns OFF',
          variant: 'outline-warning',
          badgeVariant: 'warning',
          globalButton: 'Kill ALL Campaigns',
          globalOnLabel: 'ALL campaigns are KILLED platform-wide',
          globalHelp: 'Stops every practice from sending bulk campaigns (both SMS and email). 1:1 conversations and reminders keep working.',
          stops: ['Campaign SMS blasts', 'Campaign EMAIL blasts', 'Scheduled campaigns that would fire while this is on'],
          keeps: ['1:1 patient conversations', 'Appointment reminders &amp; recall', 'Booking confirmations &amp; OTP', 'Forms, tx-presenter, everything else'],
          note: 'The narrowest switch — use this when a campaign misfires but the practice should keep operating normally.',
        },
        {
          key: 'email',
          label: 'SendGrid email',
          short: 'email',
          button: 'Kill Email',
          badge: 'Email KILLED',
          variant: 'outline-danger',
          badgeVariant: 'danger',
          globalButton: 'Kill ALL Email',
          globalOnLabel: 'ALL patient email is KILLED platform-wide',
          globalHelp: "Stops every practice's outbound patient email via SendGrid. Teamio's own alerts and internal mail are NOT affected.",
          stops: ['Appointment reminder &amp; recall emails', 'Campaign email blasts', 'Form and tx-presenter emails', 'Booking confirmation emails'],
          keeps: ['ALL texts and WhatsApp', 'Teamio&rsquo;s own alerts to the practice (low balance, invoices)', 'Everything else in Teamio'],
          note: 'Only practice&rarr;patient email is stopped. Teamio&rsquo;s internal mail never routes through this boundary.',
        },
      ],
    };
  },
  computed: {
    anyGlobalActive() {
      const g = this.status && this.status.globalKills;
      return !!(g && (g.twilio || g.campaign || g.email));
    },
  },
  async mounted() { await this.refresh(); },
  methods: {
    ...mapActions('superadmin', ['sendAction']),
    async refresh() {
      this.loading = true;
      try {
        const res = await this.sendAction({ action: 'GET_KILL_STATUS' });
        this.status = res && res.data && Array.isArray(res.data.sites) ? res.data : null;
      } catch (e) {
        this.status = null;
      } finally {
        this.loading = false;
      }
    },
    /**
     * Build the explain-exactly-what-happens popover for a button.
     *
     * An operator reaching for a kill switch is usually under pressure, and the single most
     * dangerous outcome is believing a practice is silenced when it is not. So every popover
     * spells out BOTH halves — what stops AND what keeps working — rather than describing only
     * the thing being killed. The "keeps working" list is the one that prevents a false sense of
     * safety (e.g. killing Twilio does NOT stop email, and vice versa).
     */
    popoverFor(sw, isCurrentlyKilled, isGlobal) {
      if (isCurrentlyKilled) {
        return {
          title: `Restore ${sw.short}`,
          content: `<div style="font-size:.85rem">Turns ${sw.short} back on for this practice immediately.`
            + ` Nothing else changes — the other two switches keep whatever state they are in.</div>`,
        };
      }
      const scope = isGlobal ? 'EVERY practice' : 'this practice';
      const li = (items) => items.map((i) => `<li>${i}</li>`).join('');
      return {
        title: isGlobal ? `${sw.globalButton} — all practices` : sw.button,
        content:
          `<div style="font-size:.85rem;max-width:320px">`
          + `<div class="mb-1">Applies to <strong>${scope}</strong>.</div>`
          + `<div class="text-danger"><strong>STOPS immediately:</strong></div>`
          + `<ul class="pl-3 mb-2">${li(sw.stops)}</ul>`
          + `<div class="text-success"><strong>KEEPS WORKING:</strong></div>`
          + `<ul class="pl-3 mb-2">${li(sw.keeps)}</ul>`
          + `<div class="text-muted">${sw.note} Fully reversible — press Restore to undo.</div>`
          + `</div>`,
      };
    },
    /** Which switches are currently ON for a site — a site can be in more than one state now. */
    activeKills(s) {
      return this.SWITCHES
        .filter((sw) => s.kills && s.kills[sw.key])
        .map((sw) => ({ key: sw.key, badge: sw.badge, variant: sw.badgeVariant }));
    },
    async setSite(s, sw, on) {
      // (It used to interpolate sw.tip — no switch has one, so every confirm opened with "undefined".
      // The hover popover on the button already lists what stops and what keeps working.)
      const msg = on
        ? `${sw.button} for ${s.name}?`
        : `Restore ${sw.short} for ${s.name}?`;
      const ok = await this.$bvModal.msgBoxConfirm(msg, {
        title: on ? `Confirm — ${sw.button}` : 'Restore',
        okVariant: on ? (sw.key === 'campaign' ? 'warning' : 'danger') : 'success',
        okTitle: on ? sw.button : `Restore ${sw.short}`,
        cancelTitle: 'Cancel', centered: true,
      });
      if (!ok) return;
      const reason = on ? (window.prompt('Reason (for the audit log):', '') || null) : null;
      await this.dispatch({ action: 'SET_SITE_KILL', siteID: s.siteID, switch: sw.key, on, reason });
    },
    async setGlobal(sw, on) {
      if (!on) {
        const ok = await this.$bvModal.msgBoxConfirm(
          `Restore ${sw.short} for ALL practices? This clears that switch everywhere, including sites you set individually. The other switches are untouched.`,
          { title: `Restore ${sw.short} everywhere`, okVariant: 'success', centered: true }
        );
        if (!ok) return;
      }
      const reason = on ? (window.prompt('Reason (for the audit log):', 'emergency') || 'emergency') : null;
      this.globalArming = null; this.globalConfirmText = '';
      await this.dispatch({ action: 'SET_GLOBAL_KILL', switch: sw.key, on, reason });
    },
    smsOutboundControl(s) {
      return smsOutboundControl(s);
    },
    async setSmsOutbound(s, on) {
      const msg = on
        ? `Make ${s.name} WhatsApp-only?

Nothing will be sent by SMS from this practice — replies, reminders, campaigns, links. Patients are messaged on WhatsApp. Texts patients send still arrive.`
        : `Turn texting back on for ${s.name}?`;
      const ok = await this.$bvModal.msgBoxConfirm(msg, {
        title: on ? 'Confirm — WhatsApp-only' : 'Turn texting back on',
        okVariant: on ? 'info' : 'success',
        okTitle: on ? 'Make WhatsApp-only' : 'Turn texting back on',
        cancelTitle: 'Cancel', centered: true,
      });
      if (!ok) return;
      const reason = window.prompt('Reason (for the audit log):', '') || null;
      await this.dispatch(smsOutboundPayload(s, on, reason));
    },
    // superadmin/sendAction NEVER throws on an HTTP error — it returns { status, data }. So the
    // outcome is read from the status (actionOutcome), and a refusal shows the server's sentence
    // instead of a green "applied".
    async dispatch(payload) {
      this.busy = true;
      try {
        const res = await this.sendAction(payload);
        const outcome = actionOutcome(res, 'That change was not applied. Please try again.');
        if (outcome.ok) {
          this.$toast && this.$toast.success && this.$toast.success('Messaging-safety change applied');
        } else {
          this.$toast && this.$toast.error && this.$toast.error(outcome.message);
        }
        await this.refresh();
      } catch (e) {
        this.$toast && this.$toast.error && this.$toast.error('Action failed: ' + ((e && e.message) || 'unknown'));
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.messaging-safety { border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; }
.ms-global { border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; }
.ms-global--armed { border-color: #dc2626; background: #fef2f2; }
.ms-table th { font-size: 0.75rem; color: #64748b; text-transform: uppercase; letter-spacing: 0.03em; }
.ms-table td { font-size: 0.88rem; }
.ms-policy-col { border-left: 2px solid #e2e8f0; white-space: nowrap; }
</style>
